import { useEffect, useState } from "react";
import { Package, Pencil, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useSettings } from "@/context/SettingsContext";
import { MENUS } from "@/lib/menus";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { Field, PageHeader } from "@/components/common";

const EMPTY = { name: "", cabang_limit: "", description: "" };

const PackageForm = ({ open, onOpenChange, initial, onSaved }) => {
  const [f, setF] = useState(EMPTY);
  useEffect(() => { if (open) setF(initial ? { name: initial.name, cabang_limit: String(initial.cabang_limit), description: initial.description || "" } : EMPTY); }, [open, initial]);
  const submit = async (e) => {
    e.preventDefault();
    try {
      const body = { ...f, cabang_limit: Number(f.cabang_limit) };
      if (initial) await api.put(`/packages/${initial.id}`, body); else await api.post("/packages", body);
      toast.success("Paket disimpan"); onOpenChange(false); onSaved();
    } catch (er) { toast.error(errMsg(er)); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="package-form">
        <DialogHeader><DialogTitle className="font-heading">{initial ? "Edit Paket" : "Tambah Paket"}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <Field label="Nama Paket"><Input required className="h-11" placeholder="cth. Paket Dasar" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="package-name-input" /></Field>
          <Field label="Batas Kantor Cabang"><Input required inputMode="numeric" className="h-11" value={f.cabang_limit} onChange={(e) => setF({ ...f, cabang_limit: e.target.value.replace(/\D/g, "") })} data-testid="package-limit-input" /></Field>
          <Field label="Keterangan (opsional)"><Input className="h-11" value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} data-testid="package-desc-input" /></Field>
          <DialogFooter><Button type="submit" className="rounded-full h-11 px-6" data-testid="package-submit">Simpan</Button></DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const PackageCard = ({ p, onEdit, onDelete }) => (
  <div className="panel !p-5 flex flex-col gap-3" data-testid={`package-card-${p.id}`}>
    <div className="flex items-center gap-3">
      <span className="h-11 w-11 rounded-xl bg-primary/12 text-primary grid place-items-center shrink-0"><Package className="h-5 w-5" /></span>
      <div className="min-w-0 flex-1"><p className="font-heading font-semibold truncate">{p.name}</p><p className="text-xs text-muted-foreground truncate">{p.description || "-"}</p></div>
      <Button size="sm" variant="outline" className="h-9 w-9 p-0 rounded-full" onClick={() => onEdit(p)} aria-label="Edit" data-testid={`package-edit-${p.id}`}><Pencil className="h-4 w-4" /></Button>
      <Button size="sm" variant="ghost" className="h-9 w-9 p-0 rounded-full text-rose-600" onClick={() => onDelete(p)} aria-label="Hapus" data-testid={`package-delete-${p.id}`}><Trash2 className="h-4 w-4" /></Button>
    </div>
    <div className="flex gap-4 text-sm"><span>Maks. <b data-testid={`package-limit-${p.id}`}>{p.cabang_limit}</b> kantor cabang</span><span className="text-muted-foreground">Dipakai {p.wilayah_count} wilayah</span></div>
  </div>
);

const MenuVisibility = () => {
  const { settings, apply } = useSettings();
  const [hidden, setHidden] = useState(settings.hidden_menus);
  const [busy, setBusy] = useState(false);
  useEffect(() => { setHidden(settings.hidden_menus); }, [settings.hidden_menus]);
  const save = async () => {
    setBusy(true);
    try { const { data } = await api.put("/settings/menus", { hidden_menus: hidden }); apply(data); toast.success("Visibilitas menu disimpan"); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <section className="panel" data-testid="settings-menus">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <div><h2 className="font-heading text-lg font-bold">Visibilitas Menu</h2><p className="text-sm text-muted-foreground">Sembunyikan menu yang belum dipakai untuk seluruh pengguna.</p></div>
        <Button onClick={save} disabled={busy} className="rounded-full h-10 px-5" data-testid="menus-save-button">{busy ? "Menyimpan..." : "Simpan Menu"}</Button>
      </div>
      <div className="grid sm:grid-cols-2 gap-3">
        {MENUS.filter((m) => m.hideable).map((m) => (
          <div key={m.key} className="flex items-center justify-between rounded-xl border px-4 py-3">
            <span className="flex items-center gap-3 text-sm font-medium"><m.icon className="h-4 w-4 text-muted-foreground" />{m.label}</span>
            <Switch checked={!hidden.includes(m.key)} onCheckedChange={(v) => setHidden((h) => (v ? h.filter((x) => x !== m.key) : [...h, m.key]))} data-testid={`menu-visibility-${m.key}`} />
          </div>
        ))}
      </div>
    </section>
  );
};

export default function LayananServerPage() {
  const [items, setItems] = useState(null);
  const [form, setForm] = useState({ open: false, initial: null });
  const [del, setDel] = useState(null);
  const load = () => api.get("/packages").then((r) => setItems(r.data)).catch((e) => { toast.error(errMsg(e)); setItems([]); });
  useEffect(() => { load(); }, []);
  const remove = async () => { try { await api.delete(`/packages/${del.id}`); toast.success("Paket dihapus"); load(); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <div className="max-w-4xl space-y-6">
      <PageHeader title="Layanan Server" action={<Button className="rounded-full h-11 px-6" onClick={() => setForm({ open: true, initial: null })} data-testid="add-package-button"><Plus className="h-4 w-4 mr-1" />Tambah Paket</Button>} />
      <section data-testid="packages-section">
        <h2 className="font-heading text-lg font-bold">Paket Langganan Kantor Wilayah</h2>
        <p className="text-sm text-muted-foreground mb-4">Batas jumlah kantor cabang yang bisa dibuka setiap kantor wilayah. Pilih paket saat membuat atau mengedit kantor wilayah. Wilayah tanpa paket tidak dibatasi.</p>
        {items === null ? <p className="text-muted-foreground">Memuat...</p> : items.length === 0 ? (
          <p className="panel text-sm text-muted-foreground text-center" data-testid="packages-empty">Belum ada paket.</p>
        ) : <div className="grid sm:grid-cols-2 gap-4">{items.map((p) => <PackageCard key={p.id} p={p} onEdit={(x) => setForm({ open: true, initial: x })} onDelete={setDel} />)}</div>}
      </section>
      <MenuVisibility />
      <PackageForm open={form.open} onOpenChange={(o) => setForm((f) => ({ ...f, open: o }))} initial={form.initial} onSaved={load} />
      <ConfirmDialog open={Boolean(del)} onOpenChange={(o) => !o && setDel(null)} title="Hapus Paket" danger confirmLabel="Ya, hapus" onConfirm={remove}
        description={del && <>Hapus paket <b>{del.name}</b>?</>} />
    </div>
  );
}
